/**
 * ReportAnalysisBanner — shown at the top of any page that displays
 * a reports table (Stock Detail → Reports tab, /reports admin page).
 * Tells the admin Claude is currently chewing through one or more
 * report PDFs so they don't wonder whether anything is happening.
 *
 * Accepts the list of reports as a prop; hides itself automatically
 * when no row is in the `analysing` / `pending` state.
 */
import React, { useMemo } from "react";
import { Clock } from "lucide-react";

const ReportAnalysisBanner = ({ reports = [] }) => {
  // Strict check — only reports that are ACTIVELY running through
  // Claude (analysis_status === "analysing") count. Pending scheduler
  // rows, failed rows, and "downloaded but queued" historical rows
  // are NOT shown here because they would report an inflated
  // "Claude is reading N reports" number that doesn't reflect real
  // in-flight work (the executor is capped at max_workers=1).
  const inProgressCount = useMemo(() => {
    return (reports || []).filter(
      (r) => (r.analysis_status || "").toLowerCase() === "analysing",
    ).length;
  }, [reports]);

  if (inProgressCount === 0) return null;

  const label = inProgressCount === 1
    ? "Claude is reading 1 report"
    : `Claude is reading ${inProgressCount} reports`;

  return (
    <div
      className="rounded-md border border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 px-4 py-3 text-sm flex items-start gap-3"
      data-testid="report-analysis-banner"
      role="status"
      aria-live="polite"
    >
      <div className="mt-0.5 h-8 w-8 rounded-full bg-[hsl(var(--info))]/15 grid place-items-center shrink-0">
        <Clock className="h-4 w-4 text-[hsl(var(--info))] animate-pulse" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[hsl(var(--info))] flex items-center gap-2">
          <span data-testid="report-analysis-banner-count">{label}</span>
          <span className="inline-flex h-2 w-2 rounded-full bg-[hsl(var(--info))] animate-pulse" aria-hidden="true" />
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          This takes 1–2 minutes per report. You can browse other pages safely —
          we'll notify you when the analysis completes.
        </div>
      </div>
    </div>
  );
};

export default ReportAnalysisBanner;
